import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react';
import { motion } from 'framer-motion';
import { useLocalStorage } from 'usehooks-ts';
import { t } from 'i18next';

import appConfig from '../../../../app.config';
import brandConfig from './brand.config';
import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import { selectConnection, selectPlayers } from '../../../state/selectors';
import { show as showTour } from '../../../slices/local/tour';

import { Sidebar } from './partials/Sidebar';
import { LanguageMenu } from './partials/LanguageMenu';
import { IconButton, useFullscreen } from './partials/UI';
import { themeOptions, useBrandTheme } from './partials/useBrandTheme';
import { AccountIcon, CheckIcon, ComputerIcon, DarkModeIcon, FullscreenExitIcon, FullscreenIcon, HelpIcon, LightModeIcon, LogoutIcon, MenuIcon } from './partials/Icons';

const themeIcons = { system: ComputerIcon, light: LightModeIcon, dark: DarkModeIcon };

const version = (process.env.REACT_APP_VERSION || '').replace(/^v/i, '');

const parseRoute = (location) => {
  const [, playerId, channel, stationId] = location.match(/^\/(\d+)(?:\/(\d+))?(?:\/(\d+))?\/?$/) || [];
  return {
    playerId: playerId && parseInt(playerId),
    channel: channel && parseInt(channel),
    stationId: stationId && parseInt(stationId)
  };
};

const StatusDot = ({ online }) => (
  <span
    title={online ? t('musicstyling.status.online') : t('musicstyling.status.offline')}
    className={`inline-block w-[18px] h-[18px] rounded-full shrink-0 ${online ? 'bg-ms-success' : 'bg-ms-danger'}`}
  />
);

const UserMenu = ({ customerData }) => {
  const dispatch = useAppDispatch();
  const { theme, setTheme } = useBrandTheme();

  return (
    <Menu placement="bottom-end">
      <MenuHandler>
        <span>
          <IconButton title={customerData?.Customer?.email}>
            <AccountIcon className="w-6 h-6" />
          </IconButton>
        </span>
      </MenuHandler>
      <MenuList className="min-w-56">
        {customerData?.Customer?.email && (
          <div className="px-3 pt-1 pb-2 mb-1 border-b border-ms-border text-xs text-ms-muted truncate outline-none" tabIndex={-1}>
            {customerData.Customer.email}
          </div>
        )}
        <div className="px-3 pt-1 pb-1 text-xs text-ms-muted outline-none" tabIndex={-1}>
          {t('musicstyling.theme.title')}
        </div>
        {themeOptions.map(option => {
          const Icon = themeIcons[option];
          return (
            <MenuItem key={option} className="flex items-center gap-2" onClick={() => setTheme(option)}>
              <Icon className="w-4 h-4 text-ms-muted" />
              <span className="flex-1">{t(`musicstyling.theme.${option}`)}</span>
              {theme === option && <CheckIcon className="w-4 h-4 text-ms-accent" />}
            </MenuItem>
          );
        })}
        <hr className="my-1 border-ms-border outline-none" tabIndex={-1} />
        {appConfig.showOnboarding && (
          <MenuItem className="flex items-center gap-2" onClick={() => dispatch(showTour())}>
            <HelpIcon className="w-4 h-4 text-ms-muted" />
            {t('footer.tour_guide')}
          </MenuItem>
        )}
        <Link href="/logout">
          <MenuItem className="flex items-center gap-2">
            <LogoutIcon className="w-4 h-4 text-ms-muted" />
            {t('navbar.logout')}
          </MenuItem>
        </Link>
      </MenuList>
    </Menu>
  );
};

export const MainFrame = ({ children, footer, customerData }) => {
  const [location] = useLocation();
  const route = parseRoute(location);
  const players = useAppSelector(selectPlayers);
  const connection = useAppSelector(selectConnection);
  const [collapsed, setCollapsed] = useLocalStorage('musicstyling.sidebarCollapsed', false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const fullscreen = useFullscreen();
  useBrandTheme();

  const playerList = Object.values(players);
  const player = route.playerId ? players[route.playerId] : (playerList.length === 1 ? playerList[0] : null);
  const online = !!connection?.available && (!player || player.isOnline);

  const toggleSidebar = () => {
    if (window.matchMedia('(min-width: 992px)').matches) {
      setCollapsed(!collapsed);
    } else {
      setMobileOpen(!mobileOpen);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="flex flex-col h-dvh overflow-hidden bg-ms-bg text-ms-text">
        <div className="flex flex-1 min-h-0">
          <aside className={`w-[360px] shrink-0 ${collapsed ? 'hidden' : 'hidden lg:block'}`}>
            <Sidebar route={route} />
          </aside>

          {mobileOpen && (
            <div className="lg:hidden fixed inset-0 z-200 flex">
              <div className="w-[360px] max-w-[85vw] h-full shadow-2xl">
                <Sidebar route={route} onNavigate={() => setMobileOpen(false)} />
              </div>
              <button type="button" aria-label={t('musicstyling.nav.toggle_menu')} className="flex-1 bg-black/60" onClick={() => setMobileOpen(false)} />
            </div>
          )}

          <div className="flex flex-1 flex-col min-w-0">
            <header className="h-16 shrink-0 flex items-center gap-2 px-3 md:px-4">
              <IconButton onClick={toggleSidebar} title={t('musicstyling.nav.toggle_menu')}>
                <MenuIcon className="w-6 h-6" />
              </IconButton>
              <div className="flex flex-1 items-center gap-3 min-w-0 ml-1">
                <h2 className="text-[22px] font-normal text-ms-text truncate">
                  <span className="hidden sm:inline">{brandConfig.title}</span>
                  <span className="sm:hidden">DLP</span>
                  {version && ` - v ${version}`}
                </h2>
                <StatusDot online={online} />
              </div>
              {fullscreen.supported && (
                <IconButton
                  onClick={fullscreen.toggle}
                  title={fullscreen.isFullscreen ? t('musicstyling.footer.exit_fullscreen') : t('musicstyling.footer.fullscreen')}
                  className="max-sm:hidden"
                >
                  {fullscreen.isFullscreen ? <FullscreenExitIcon className="w-6 h-6" /> : <FullscreenIcon className="w-6 h-6" />}
                </IconButton>
              )}
              {appConfig.features.languageSwitcher && <LanguageMenu />}
              <UserMenu customerData={customerData} />
            </header>

            <div className="relative flex flex-1 flex-col min-h-0">
              {children}
            </div>
          </div>
        </div>

        {footer}
      </div>
    </motion.div>
  );
};
